import { FunctionComponent, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FeaturesType } from '@follow/cdk'
import { useDictationContext } from '../../../core/provider/dictation-context.provider'
import { useUserEnabledFeature } from '../../../hooks/utils/user'
import { useWidthObserver } from '../../../hooks/utils'
import { breakSmall } from '../../../misc/responsive'
import { TrackingService } from '../../../misc/Tracking'
import { hasAssistantRole } from '../../../misc/roles.utilities'
import { loggedUserSelector } from '../../../store/domain/me/me.selectors'
import { useStatsAssistantChatContext } from '../../statsAssistant/StatsAssistantChat.context'
import { StatsChatPanel } from '../../statsAssistant/StatsChatPanel'
import { useStatsAssistantEnabled } from '../../../hooks/utils/statsAssistant'
import { DictationBar } from './DictationBar.component'
import { DictationNotepad } from './DictationNotepad.component'
import { BAR_HEIGHT, STATS_PANEL_HEIGHT, STATS_PANEL_WIDTH } from './DictationWidget.types'
import { ModeSelector } from './ModeSelector.component'
import { StatsPanelModeSwitch } from './StatsPanelModeSwitch.component'
import { StatsModeBar } from './StatsModeBar.component'
import { useWidgetState } from './useWidgetState.hook'
import styles from './DictationWidget.module.scss'

const VIEWPORT_MARGIN = 16

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

/**
 * Le panneau Statistiques s'ancre sur le bas de la pastille, puis est ramené dans l'écran
 * (vers le bas s'il dépasse en haut). Décalage relatif à la position de la pastille.
 */
const getStatsPanelOffset = (position: { x: number; y: number }) => ({
  x:
    clamp(position.x, VIEWPORT_MARGIN, window.innerWidth - STATS_PANEL_WIDTH - VIEWPORT_MARGIN) -
    position.x,
  y:
    clamp(
      position.y + BAR_HEIGHT - STATS_PANEL_HEIGHT,
      VIEWPORT_MARGIN,
      window.innerHeight - STATS_PANEL_HEIGHT - VIEWPORT_MARGIN,
    ) - position.y,
})

export const DictationWidget: FunctionComponent = () => {
  const voiceDictationEnabled = useUserEnabledFeature(FeaturesType.voiceDictation)
  const statsModeEnabled = useStatsAssistantEnabled()
  const statsChat = useStatsAssistantChatContext()
  const { isWidgetHidden } = useDictationContext()
  const isMobile = useWidthObserver(breakSmall)
  const loggedUser = useSelector(loggedUserSelector)
  const isAssistant = hasAssistantRole(loggedUser?.roles ?? [])
  const voiceEnabled = Boolean(voiceDictationEnabled)
  const widgetEnabled = voiceEnabled || statsModeEnabled
  const isRendered =
    Boolean(loggedUser) && widgetEnabled && !isWidgetHidden && !isMobile && !isAssistant
  const w = useWidgetState({
    statsModeEnabled,
    voiceDictationEnabled: voiceEnabled,
    isRendered,
  })

  useEffect(() => {
    TrackingService.setUserProperty('dictation_enabled', voiceDictationEnabled ?? false)
  }, [voiceDictationEnabled])

  const isStatsMode = w.mode === 'stats'
  const isStatsCompactOpen =
    isStatsMode && w.statsPanelOpen && statsChat !== null && !statsChat.isExpanded

  if (!isRendered || !w.activeMode) return null

  const isNotepadOpen = w.mode === 'notepad'
  const statsPanelOffset = getStatsPanelOffset(w.position)

  return (
    <div
      ref={w.widgetRef}
      className={styles.widget}
      style={{ left: w.position.x, top: w.position.y }}
    >
      <div className={styles.barWrapper}>
        {isNotepadOpen && w.panelDirection === 'up' && (
          <DictationNotepad
            transcript={w.transcript}
            copied={w.copied}
            panelDirection={w.panelDirection}
            onTranscriptChange={w.setTranscript}
            onCopy={w.handleCopy}
            onReset={w.handleReset}
          />
        )}

        {isStatsCompactOpen && statsChat ? (
          <div
            className={styles.statsPanelAnchor}
            style={{ left: statsPanelOffset.x, top: statsPanelOffset.y }}
          >
            <StatsChatPanel
              chat={statsChat}
              onHeaderMouseDown={w.onMouseDown}
              consumeDrag={w.consumeDrag}
              headerStart={
                <StatsPanelModeSwitch
                  activeMode={w.activeMode}
                  canSwitch={w.availableModes.length > 1}
                  modeOpen={w.modeOpen}
                  onToggle={() => w.handleDropdownToggle('mode')}
                />
              }
            />
            {w.modeOpen && (
              <ModeSelector
                modes={w.availableModes}
                activeModeId={w.activeMode.id}
                direction="down"
                onSelect={w.handleModeChange}
                onClose={() => w.setModeOpen(false)}
              />
            )}
          </div>
        ) : isStatsMode ? (
          <StatsModeBar
            activeMode={w.activeMode}
            availableModes={w.availableModes}
            hasProcessingTurn={w.hasBackgroundStatsTurn}
            hasUnseenResult={w.hasUnseenStatsResult}
            panelDirection="up"
            modeOpen={w.modeOpen}
            onMouseDown={w.onMouseDown}
            consumeDrag={w.consumeDrag}
            onPanelToggle={w.handleStatsPanelToggle}
            onExpand={w.handleStatsPanelOpen}
            onModeDropdownToggle={() => w.handleDropdownToggle('mode')}
            onModeChange={w.handleModeChange}
            setModeOpen={w.setModeOpen}
          />
        ) : (
          <DictationBar
            mode={w.mode}
            activeMode={w.activeMode}
            availableModes={w.availableModes}
            activeMic={w.activeMic}
            devices={w.devices}
            selectedDeviceId={w.selectedDeviceId}
            modeOpen={w.modeOpen}
            micOpen={w.micOpen}
            isDisabled={w.isDisabled}
            isRecording={w.isRecording}
            buttonState={w.buttonState}
            panelDirection={w.panelDirection}
            isPanelOpen={isNotepadOpen}
            onMouseDown={w.onMouseDown}
            onRecordToggle={w.handleRecordToggle}
            onDropdownToggle={w.handleDropdownToggle}
            onModeChange={w.handleModeChange}
            onMicSelect={w.handleMicSelect}
            onSettingsClick={w.handleSettingsClick}
            setModeOpen={w.setModeOpen}
            setMicOpen={w.setMicOpen}
          />
        )}

        {isNotepadOpen && w.panelDirection === 'down' && (
          <DictationNotepad
            transcript={w.transcript}
            copied={w.copied}
            panelDirection={w.panelDirection}
            onTranscriptChange={w.setTranscript}
            onCopy={w.handleCopy}
            onReset={w.handleReset}
          />
        )}
      </div>
    </div>
  )
}
